import { jsx as _jsx } from "preact/jsx-runtime";
import classNames from 'classnames';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, } from 'preact/hooks';
import { stopEscapePropagation } from '../../ModalDialog.js';
const GAP = 4;
/**
 * The open actions menu for one item: a `popover="auto"` element in the top
 * layer, positioned from the trigger's viewport rect (flipping upwards near
 * the bottom), so no ancestor can clip it. Light-dismiss, Escape and focus
 * return are the browser's; arrow keys move between entries. Engines without
 * the Popover API get a plain positioned menu that closes on Escape/Tab/action.
 */
export default function ItemActionsPopover({ file, actions, anchor, containerRef, runAction, onClose, i18n, }) {
    const menuRef = useRef(null);
    const [position, setPosition] = useState(null);
    // Position from the trigger's live viewport rect, re-measured whenever the
    // page scrolls or resizes so the menu never detaches from its trigger. The
    // flip needs the menu's height, which a closed `[popover]` (display: none)
    // reports as 0. The menu lines up with the trigger's inline-end edge, and
    // opens towards the row: leftwards, or rightwards in a right-to-left UI.
    const reposition = useCallback(() => {
        const menu = menuRef.current;
        if (!menu)
            return;
        const a = anchor.getBoundingClientRect();
        const height = menu.offsetHeight;
        const fitsBelow = a.bottom + GAP + height <= window.innerHeight;
        const top = fitsBelow ? a.bottom + GAP : Math.max(0, a.top - GAP - height);
        setPosition(getComputedStyle(anchor).direction === 'rtl'
            ? { top, left: Math.max(0, a.left) }
            : { top, right: Math.max(0, window.innerWidth - a.right) });
    }, [anchor]);
    // A first placement before paint, which also lifts `visibility: hidden` so
    // the effect below can focus an entry; that effect measures again once the
    // popover is shown.
    useLayoutEffect(reposition, [reposition]);
    useEffect(() => {
        const menu = menuRef.current;
        const container = containerRef.current;
        if (!menu)
            return;
        // Mirror the browser's light-dismiss / Escape into our state.
        const onToggle = (event) => {
            if (event.newState === 'closed')
                onClose();
        };
        const controller = new AbortController();
        const { signal } = controller;
        menu.addEventListener('toggle', onToggle, { signal });
        if (typeof menu.showPopover === 'function')
            menu.showPopover();
        // Showing the popover gives it a real height: correct the estimate.
        reposition();
        const onPageScroll = (event) => {
            // The scrolling list closes the menu; anything else (the host page)
            // moves the trigger, so the menu follows it.
            if (container &&
                event.target instanceof Node &&
                container.contains(event.target)) {
                onClose();
                return;
            }
            reposition();
        };
        window.addEventListener('scroll', onPageScroll, { capture: true, signal });
        window.addEventListener('resize', reposition, { signal });
        // `preventScroll`: the Dashboard panel is an overflow:hidden container that
        // would otherwise be scrolled (mid slide-in animation) to reveal the item.
        menu
            .querySelector('[role="menuitem"]')
            ?.focus({ preventScroll: true });
        return () => controller.abort();
    }, [containerRef, onClose, reposition]);
    const closeAndRefocus = () => {
        onClose();
        anchor.focus({ preventScroll: true });
    };
    const onKeyDown = (event) => {
        const items = Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]') ?? []);
        const index = items.indexOf(document.activeElement);
        const focusAt = (i) => {
            event.preventDefault();
            items[(i + items.length) % items.length]?.focus({ preventScroll: true });
        };
        switch (event.key) {
            case 'Escape':
                // The browser hides a popover itself.
                stopEscapePropagation(event);
                if (typeof menuRef.current?.hidePopover !== 'function')
                    closeAndRefocus();
                break;
            case 'ArrowDown':
                focusAt(index + 1);
                break;
            case 'ArrowUp':
                focusAt(index - 1);
                break;
            case 'Home':
                focusAt(0);
                break;
            case 'End':
                focusAt(items.length - 1);
                break;
            case 'Tab':
                onClose();
                break;
            default:
        }
    };
    return (_jsx("div", { ref: menuRef, className: "uppy-ProviderBrowserItem-actionsMenu", popover: "auto", role: "menu", "aria-label": i18n('itemActionsNamed', {
            name: file.data.name ?? i18n('unnamed'),
        }), style: position
            ? {
                top: `${position.top}px`,
                ...('left' in position
                    ? { left: `${position.left}px` }
                    : { right: `${position.right}px` }),
            }
            : { visibility: 'hidden' }, onKeyDown: onKeyDown, children: actions.map((action) => (_jsx("button", { type: "button", role: "menuitem", className: classNames('uppy-u-reset uppy-c-btn uppy-ProviderBrowserItem-actionsMenuItem', action.danger && 'uppy-ProviderBrowserItem-actionsMenuItem--danger'), onClick: (event) => {
                event.stopPropagation();
                event.preventDefault();
                closeAndRefocus();
                runAction(action, file);
            }, children: action.label }, action.id))) }));
}
